<script setup>
import { computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { getCookie, setCookie } from '@/utils/cookie'

const props = defineProps({
  modelValue: { type: Boolean, default: true },
  popup: { type: Object, required: true }
})

const emit = defineEmits(['update:modelValue', 'close'])

const id = computed(() => props.popup?.popupId || '')
const html = computed(() => props.popup?.contentHtml || '')
const contType = computed(() => String(props.popup?.contType || ''))
const fileUrl = computed(() => String(props.popup?.fileUrl || ''))
const title = computed(() => props.popup?.title || '')
const stopVewAt = computed(() => props.popup?.stopVewAt === 'Y')
const expireDays = computed(() => Number(props.popup?.expireDays || 1))

// 쿠키 차단 여부 확인 (LayerPopup과 동일 패턴)
function isBlockedToday() {
  if (!stopVewAt.value || !id.value) return false
  return getCookie(`POP_STOP_${id.value}`) === '1'
}

// postMessage 핸들러 참조 (cleanup용)
let messageHandler = null

// HTML 문자열 생성 (computed에서 처리)
const htmlString = computed(() => {
  const height = props.popup?.height || 600
  const contentHeight = height - 35 - 20 // 높이 - bottom bar(35px) - 브라우저 UI여유(20px)
  const popupId = id.value
  const days = expireDays.value
  const contentMarkup = contType.value === '2' && fileUrl.value
    ? `<iframe frameborder="0" src="${fileUrl.value}" style="width:100%;height:100%;border:0;" title="${title.value}"></iframe>`
    : html.value

  const htmlParts = [
    '<!DOCTYPE html>',
    '<html lang="ko">',
    '<head>',
    '<meta http-equiv="content-type" content="text/html; charset=utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1">',
    '<title>' + title.value + '</title>',
    '<script type="text/javascript">',
    'function fnPopupCheck() {',
    '  if (window.opener) {',
    '    window.opener.postMessage({type: "popup-cookie-set", popupId: "' + popupId + '", expireDays: ' + days + '}, "*");',
    '  }',
    '  window.close();',
    '}',
    '<' + '/script>',
    '<style type="text/css">',
    'html, body { width: 100%; height: 100%; margin: 0; padding: 0; }',
    '#contents { width: 100%; height: ' + contentHeight + 'px; overflow: auto; }',
    '#contents img { width: auto; max-width: 100%; height: auto; }',
    '#bottom { position: fixed; bottom: 0; left: 0; width: 100%; height: 35px; line-height: 35px; font-size: 9pt; text-align: center; background: #222; color: #fff; }',
    '#bottom input { vertical-align: middle; }',
    '<' + '/style>',
    '<' + '/head>',
    '<body>',
    '<div id="contents">',
    '<noscript class="noScriptTitle">자바스크립트를 지원하지 않는 브라우저에서는 일부 기능을 사용하실 수 없습니다.<' + '/noscript>',
    contentMarkup,
    '<' + '/div>',
    '<div id="bottom">',
    (stopVewAt.value ? '오늘 하루 보지 않기  <input type="checkbox" name="chkPopup" value="" onclick="fnPopupCheck()" title="다음부터창열지않기체크">' : ''),
    '<' + '/div>',
    '<' + '/body>',
    '<' + '/html>'
  ]
  return htmlParts.join('')
})

// window 팝업 열기
function openWindowPopup() {
  if (!props.modelValue || (!html.value && !fileUrl.value)) return

  // 쿠키 차단 체크 — "오늘 하루 보지 않기" 설정 시 열지 않음
  if (isBlockedToday()) {
    emit('update:modelValue', false)
    return
  }

  const width = props.popup?.width || 800
  const height = props.popup?.height || 600
  // 팝업 좌표는 부모 브라우저 창 기준 상대좌표로 계산 (듀얼 모니터 대응)
  const popupLeft = parseInt(props.popup?.left ?? 0)
  const popupTop = parseInt(props.popup?.top ?? 0)
  const left = window.screenX + popupLeft
  const top = window.screenY + popupTop
  const features = `width=${width},height=${height},left=${left},top=${top},screenX=${left},screenY=${top},toolbar=no,status=no,location=no,scrollbars=yes,menubar=no,resizable=yes`
  const popupId = id.value

  // 기존 핸들러 정리
  if (messageHandler) {
    window.removeEventListener('message', messageHandler)
  }

  // 팝업 윈도우에서 쿠키 설정을 감지하기 위해 postMessage 리스너 추가
  messageHandler = (event) => {
    if (event.data?.type === 'popup-cookie-set' && event.data?.popupId === popupId) {
      const days = event.data?.expireDays || expireDays.value || 1
      setCookie(`POP_STOP_${popupId}`, '1', { days })
      // 리스너 정리
      window.removeEventListener('message', messageHandler)
      messageHandler = null
    }
  }
  window.addEventListener('message', messageHandler)

  try {
    const openedWindow = window.open('about:blank', popupId || 'popup', features)
    if (openedWindow) {
      openedWindow.document.open()
      openedWindow.document.write(htmlString.value)
      openedWindow.document.close()
      openedWindow.focus()
      // 팝업이 닫힐 때 리스너 정리
      const checkClosed = setInterval(() => {
        if (openedWindow.closed) {
          clearInterval(checkClosed)
          if (messageHandler) {
            window.removeEventListener('message', messageHandler)
            messageHandler = null
          }
        }
      }, 500)
    }
  } catch {
    // 팝업 차단 등
  }

  // 팝업이 열렸으므로 상태 초기화
  emit('update:modelValue', false)
}

// 컴포넌트 정리
onBeforeUnmount(() => {
  if (messageHandler) {
    window.removeEventListener('message', messageHandler)
    messageHandler = null
  }
})

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    await nextTick()
    openWindowPopup()
  }
}, { immediate: true })
</script>

<template>
  <!-- 윈도우 팝업은 렌더링 없음 (window.open()으로 처리) -->
  <span v-show="false" />
</template>
